import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Chart } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mergeRefundModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

const COLOR_AMOUNT = "#5CC7C1";
const COLOR_QTY = "#F9BC44";

function formatAmountCell(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

function formatAmountAxisTick(value) {
  const v = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(v)) return "";
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return String(Math.round(v));
}

/** Integer-only max + step for Qty (left) axis — avoids fractional ticks (e.g. 0.6, 1.2). */
function qtyAxisIntegerMaxAndStep(maxQty, tickTarget = 6) {
  const m = Math.max(0, Number(maxQty) || 0);
  const padded = Math.max(1, Math.ceil(m * 1.25));
  const naive = Math.ceil(padded / Math.max(2, tickTarget - 1));
  let step = Math.max(1, naive);
  const nice = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000, 20000, 25000, 50000, 100000];
  const hit = nice.find((n) => n >= naive);
  if (hit != null) step = hit;
  const axisMax = Math.max(padded, Math.ceil(padded / step) * step);
  return { max: axisMax, stepSize: step };
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CustomOrderChartModel; cardTitle: string }} props
 */
function RefundChartBody({ model, cardTitle }) {
  const { t } = useTranslation();

  const { chartData, options } = useMemo(() => {
    const labels = model.categories ?? [];
    const amount = model.amount ?? [];
    const qty = model.qty ?? [];
    const n = labels.length;
    const amountN = Array.from({ length: n }, (_, i) => Number(amount[i]) || 0);
    const qtyN = Array.from({ length: n }, (_, i) => Number(qty[i]) || 0);

    const dsAmount = t("Amount", { defaultValue: "Amount" });
    const dsQty = t("Qty", { defaultValue: "Qty" });

    const peakAmt = Math.max(60000, ...amountN, 0);
    const amtMax = Math.ceil(peakAmt / 15000) * 15000;

    const maxQ = Math.max(0, ...qtyN, 0);
    const { max: qtyCap, stepSize: qtyStep } = qtyAxisIntegerMaxAndStep(maxQ);

    const data = {
      labels,
      datasets: [
        {
          type: "bar",
          label: dsAmount,
          data: amountN,
          backgroundColor: COLOR_AMOUNT,
          yAxisID: "y1",
          order: 2,
          maxBarThickness: 36,
          borderRadius: 0,
          borderSkipped: false,
        },
        {
          type: "line",
          label: dsQty,
          data: qtyN,
          borderColor: COLOR_QTY,
          backgroundColor: COLOR_QTY,
          yAxisID: "y",
          order: 0,
          tension: 0,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          pointBackgroundColor: COLOR_QTY,
          pointBorderColor: COLOR_QTY,
        },
      ],
    };

    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      layout: {
        padding: {
          top: 4,
          right: 6,
          left: 2,
          bottom: 4,
        },
      },
      datasets: {
        bar: {
          categoryPercentage: 0.72,
          barPercentage: 0.85,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#2A2A2A",
          bodyColor: "#2A2A2A",
          borderColor: "#D9D9D9",
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          displayColors: true,
          titleFont: { family: "'Segoe UI', Roboto, sans-serif", size: 12, weight: "600" },
          bodyFont: { family: "'Segoe UI', Roboto, sans-serif", size: 12, weight: "400" },
          callbacks: {
            title: (items) => (items[0] ? String(items[0].label) : ""),
            label: (ctx) => {
              const y = ctx.parsed?.y ?? 0;
              if (ctx.datasetIndex === 0) {
                return ` ${dsAmount}: ${formatAmountCell(y)}`;
              }
              return ` ${dsQty}: ${Math.round(Number(y) || 0)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
            maxRotation: n > 7 ? 35 : 0,
            minRotation: 0,
          },
          border: { display: false },
        },
        y: {
          position: "left",
          min: 0,
          max: qtyCap,
          title: {
            display: true,
            text: dsQty,
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 12 },
          },
          ticks: {
            stepSize: qtyStep,
            precision: 0,
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
            callback: (v) => {
              const n = typeof v === "number" ? v : Number(v);
              if (!Number.isFinite(n)) return "";
              return String(Math.round(n));
            },
          },
          grid: {
            color: "#E6E6E6",
            drawTicks: false,
          },
          border: { display: false },
        },
        y1: {
          position: "right",
          min: 0,
          suggestedMax: amtMax,
          title: {
            display: true,
            text: dsAmount,
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 12 },
          },
          grid: { drawOnChartArea: false },
          ticks: {
            stepSize: 15000,
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
            callback: (v) => formatAmountAxisTick(v),
          },
          border: { display: false },
        },
      },
    };

    return { chartData: data, options: opts };
  }, [model, t]);

  return (
    <div className="pos-refund-chart" aria-label={cardTitle}>
      <div className="pos-refund-chart__plot">
        <Chart type="bar" data={chartData} options={options} />
      </div>
      <div className="pos-refund-legend" role="list" aria-label={t("Refund legend", { defaultValue: "Refund legend" })}>
        <div className="pos-refund-legend__item" role="listitem">
          <span className="pos-refund-legend__amount-dot" aria-hidden />
          <span>{t("Amount", { defaultValue: "Amount" })}</span>
        </div>
        <div className="pos-refund-legend__item" role="listitem">
          <span className="pos-refund-legend__qty-line-circle" aria-hidden />
          <span>{t("Qty", { defaultValue: "Qty" })}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CustomOrderChartModel; cardTitle: string; rowIdPrefix: string }} props
 */
function RefundTableBody({ model, cardTitle, rowIdPrefix }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {"date"|"qty"|"customer"|"amount"} */ ("amount"),
    dir: /** @type {"asc"|"desc"} */ ("desc"),
  }));

  useEffect(() => {
    setSort({ key: "amount", dir: "desc" });
  }, [model]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const baseRows = useMemo(() => {
    const cat = model?.categories ?? [];
    const amt = model?.amount ?? [];
    const q = model?.qty ?? [];
    const c = model?.customer ?? [];
    const n = cat.length;
    const rows = [];
    for (let i = 0; i < n; i += 1) {
      rows.push({
        key: `${rowIdPrefix}-row-${i}-${cat[i]}`,
        dateLabel: cat[i] ?? "",
        amount: Number(amt[i]) || 0,
        qty: Math.round(Number(q[i]) || 0),
        customer: Math.round(Number(c[i]) || 0),
      });
    }
    return rows;
  }, [model, rowIdPrefix]);

  const rows = useMemo(() => {
    const copy = baseRows.map((r) => ({ ...r }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "date":
          cmp = String(a.dateLabel).localeCompare(String(b.dateLabel), undefined, { sensitivity: "base" });
          break;
        case "qty":
          cmp = a.qty - b.qty;
          break;
        case "customer":
          cmp = a.customer - b.customer;
          break;
        case "amount":
          cmp = a.amount - b.amount;
          break;
        default:
          cmp = 0;
      }
      return cmp * mul;
    });
    return copy;
  }, [baseRows, sort]);

  const totals = useMemo(() => {
    let amount = 0;
    let qty = 0;
    let customer = 0;
    for (const r of baseRows) {
      amount += r.amount;
      qty += r.qty;
      customer += r.customer;
    }
    return { amount, qty, customer };
  }, [baseRows]);

  const thBase = "pos-custom-order-table__th";
  const thSorted = "pos-custom-order-table__th--sorted";

  return (
    <div className="pos-custom-order-table" role="table" aria-label={cardTitle}>
      <div className="pos-custom-order-table__head" role="rowgroup">
        <div className="pos-custom-order-table__tr pos-custom-order-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-custom-order-table__th--category"
            ariaSortLabel={t("Sort by date", { defaultValue: "Sort by date" })}
            sorted={sort.key === "date"}
            direction={sort.dir}
            onSort={() => toggleSort("date")}
          >
            {t("Date", { defaultValue: "Date" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-custom-order-table__th--num"
            ariaSortLabel={t("Sort by quantity", { defaultValue: "Sort by quantity" })}
            sorted={sort.key === "qty"}
            direction={sort.dir}
            onSort={() => toggleSort("qty")}
          >
            {t("Qty", { defaultValue: "Qty" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-custom-order-table__th--num"
            ariaSortLabel={t("Sort by customer", { defaultValue: "Sort by customer" })}
            sorted={sort.key === "customer"}
            direction={sort.dir}
            onSort={() => toggleSort("customer")}
          >
            {t("Customer", { defaultValue: "Customer" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-custom-order-table__th--num pos-custom-order-table__th--last"
            ariaSortLabel={t("Sort by amount", { defaultValue: "Sort by amount" })}
            sorted={sort.key === "amount"}
            direction={sort.dir}
            onSort={() => toggleSort("amount")}
          >
            {t("Amount", { defaultValue: "Amount" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-custom-order-table__scroll" role="rowgroup">
        {rows.map((row) => (
          <div key={row.key} className="pos-custom-order-table__tr" role="row">
            <div className="pos-custom-order-table__td pos-custom-order-table__td--category" role="cell">
              {row.dateLabel}
            </div>
            <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
              {row.qty}
            </div>
            <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
              {row.customer}
            </div>
            <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
              {formatAmountCell(row.amount)}
            </div>
          </div>
        ))}
        <div className="pos-custom-order-table__tr pos-custom-order-table__tr--total" role="row">
          <div className="pos-custom-order-table__td pos-custom-order-table__td--category" role="cell">
            {t("Total", { defaultValue: "Total" })}
          </div>
          <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
            {totals.qty}
          </div>
          <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
            {totals.customer}
          </div>
          <div className="pos-custom-order-table__td pos-custom-order-table__td--num" role="cell">
            {formatAmountCell(totals.amount)}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Refund-style cards — Amount bars + Qty line (dual Y) + sortable table (Date / Qty / Customer / Amount).
 * Same chrome and inner layout; {@link cardBem} selects `pos-dashboard-chart-card--*` for theming.
 *
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").CustomOrderChartModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 * @param {(patch: import("./dashboardChartsModel.js").CustomOrderChartModel | null | undefined) => import("./dashboardChartsModel.js").CustomOrderChartModel} props.mergeModel
 * @param {string} props.titleKey
 * @param {string} props.defaultTitle
 * @param {string} props.downloadPrefix PNG filename prefix
 * @param {string} props.rowIdPrefix stable row keys in table mode
 * @param {string} props.cardBem BEM suffix e.g. `refund`, `reserved`, `exchange`, `wishlist`
 */
export function RefundStyleChartCard({ model, onMenuRemove, mergeModel, titleKey, defaultTitle, downloadPrefix, rowIdPrefix, cardBem }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewMode, setViewMode] = useState(/** @type {"chart"|"table"} */ ("chart"));
  const merged = useMemo(() => mergeModel(model && typeof model === "object" ? model : null), [model, mergeModel]);
  const cardTitle = t(titleKey, { defaultValue: defaultTitle });

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename(downloadPrefix));
  }, [downloadPrefix]);

  const mod = cardBem;

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--${mod}${
        viewMode === "table" ? ` pos-dashboard-chart-card--${mod}-table` : ""
      }`}
    >
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">{cardTitle}</h2>
        <div className="pos-dashboard-chart-card__header-actions">
          <DashboardChartCardKebabMenu
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
            showViewToggle
          />
        </div>
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--${mod}${
          viewMode === "table" ? ` pos-dashboard-chart-card__body--${mod}-table` : ""
        }`}
      >
        <div ref={captureRef} className={`pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--${mod}`}>
          {viewMode === "chart" ? <RefundChartBody model={merged} cardTitle={cardTitle} /> : null}
          {viewMode === "table" ? <RefundTableBody model={merged} cardTitle={cardTitle} rowIdPrefix={rowIdPrefix} /> : null}
        </div>
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").CustomOrderChartModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function RefundChartCard({ model, onMenuRemove }) {
  return (
    <RefundStyleChartCard
      model={model}
      onMenuRemove={onMenuRemove}
      mergeModel={mergeRefundModel}
      titleKey="graphRefund"
      defaultTitle="Refund"
      downloadPrefix="refund"
      rowIdPrefix="rf"
      cardBem="refund"
    />
  );
}

export default RefundChartCard;
